import { SignerAppReadyResponse } from '@dropbox/sign-internal-client';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { redirectTo } from 'signer-app/utils/redirect';
import { useReady } from 'signer-app/utils/signer-app-ready';
import { unreachable } from 'signer-app/utils/unreachable';
import type { AccessCodeResponse } from './use-signer-auth';

type UseSignerReadyParams = {
  tsmGuid: string;
  accessCode: string | null;
  authState: AccessCodeResponse | null;
  setAuthState: React.Dispatch<React.SetStateAction<AccessCodeResponse | null>>;
  setSmsMeta: (meta: any | null) => void;
  setNumAttemptsLeft: (num: number) => void;
};

export function useSignerReady({
  tsmGuid,
  accessCode,
  authState,
  setAuthState,
  setSmsMeta,
  setNumAttemptsLeft,
}: UseSignerReadyParams) {
  const ready = useReady();

  const signerReadyBackoffTries = React.useRef({
    tries: 0,
    next: 0,
  });

  const { data: readyData, isFetching: readyIsFetching } = useQuery({
    queryKey: ['signer-experience', 'ready', tsmGuid, accessCode] as const,
    queryFn: () => {
      const codeToSend = accessCode ?? undefined;
      return ready({ tsmGuid, accessCode: codeToSend });
    },
    onSuccess(readyResponseData) {
      const code = readyResponseData?.error?.code;

      if (
        (code === 'sms_rate_limit' || code === 'sms_auth_required') &&
        (readyResponseData as any)?.error?.data
      ) {
        setSmsMeta((readyResponseData as any).error?.data);
      }
      const newAttemptsLeft =
        (readyResponseData as SignerAppReadyResponse).error?.data
          ?.numAttemptsLeft ?? 0;
      setNumAttemptsLeft(newAttemptsLeft);

      if (code === 'sms_rate_limit') {
        setAuthState('sms-rate-limit');
        return;
      }

      setAuthState((prev: AccessCodeResponse | null) => {
        if (prev === 'sms-rate-limit') {
          return prev;
        } else if (code === undefined) {
          return null;
        } else if (code === 'sms_auth_required') {
          return 'need-sms-auth';
        } else if (code === 'access_code_required') {
          return accessCode !== null
            ? 'invalid-access-code'
            : 'need-access-code';
        } else if (
          code === 'access_code_blocked' ||
          code === 'too_many_requests'
        ) {
          return 'too-many-retries';
        } else if (code === 'need_signer_choice') {
          return prev;
        } else {
          unreachable(code);
          return prev;
        }
      });
    },
    refetchInterval(data) {
      if (authState || data?.error?.code) return false;
      if (data?.isInFlightEditing) {
        redirectTo('/info/externalCantSignNow');
      }
      if (!data?.ready) {
        const now = Date.now();
        signerReadyBackoffTries.current.tries += 1;
        if (now >= signerReadyBackoffTries.current.next) {
          signerReadyBackoffTries.current.next =
            now + 5000 * signerReadyBackoffTries.current.tries;
        }

        const readyBackoff = signerReadyBackoffTries.current.next - now;
        return readyBackoff;
      }
      return false;
    },
  });

  return {
    readyData,
    readyIsFetching,
  };
}
